import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    Drawer,
    Segmented,
    List,
    Empty,
    Spin,
    Pagination,
    Typography,
    Alert,
    Button,
    Flex,
    theme as antTheme,
} from 'antd';
import {
    AudioOutlined,
    DownloadOutlined,
    FileOutlined,
    FileTextOutlined,
    LinkOutlined,
    PictureOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';
import { useChatMessageAssets } from '@hooks/chat/useChat';
import { resolveChatMediaUrl } from '@utils/chatMediaUrl.util';
import { getFileIconSrc } from '@utils/fileIcon.util';
import { motion } from 'framer-motion';

const PAGE_LIMIT = 50;

const ASSET_TAB_KEYS = ['media', 'docs', 'links'];

const ASSET_TAB_CONFIG = [
    { key: 'media', label: 'Media', icon: <PictureOutlined /> },
    { key: 'docs', label: 'Docs', icon: <FileTextOutlined /> },
    { key: 'links', label: 'Links', icon: <LinkOutlined /> },
];

function formatAssetTime(ts) {
    if (!ts) return '';
    try {
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return '';
    }
}

/** Objects that often hold the real file fields on chat asset rows. */
function attachmentSources(item) {
    if (!item || typeof item !== 'object') return [];
    const nested = [item.file, item.attachment, item.media, item.asset, item.metadata].filter(
        (x) => x && typeof x === 'object',
    );
    return [item, ...nested];
}

function pickUrl(item) {
    for (const src of attachmentSources(item)) {
        const u = String(
            src.url ??
                src.fileUrl ??
                src.file_url ??
                src.path ??
                src.href ??
                src.link ??
                src.targetUrl ??
                src.target_url ??
                src.pageUrl ??
                src.page_url ??
                src.src ??
                src.downloadUrl ??
                src.download_url ??
                src.filePath ??
                src.file_path ??
                '',
        ).trim();
        if (u) return u;
    }
    return '';
}

function pickName(item) {
    for (const src of attachmentSources(item)) {
        const n =
            src.filename ??
            src.fileName ??
            src.file_name ??
            src.originalFilename ??
            src.original_filename ??
            src.name ??
            src.title ??
            src.linkTitle ??
            src.link_title ??
            src.headline ??
            src.displayName ??
            src.display_name ??
            src.label ??
            '';
        const s = String(n || '').trim();
        if (s) return s;
    }
    return 'Attachment';
}

/** Real MIME only — do not use message-level `type` ("media", "text", …). */
function pickMimeType(item) {
    for (const src of attachmentSources(item)) {
        const m = String(src.mimeType ?? src.mime_type ?? '').trim();
        if (m.includes('/')) return m;
        const t = String(src.type ?? '').trim();
        if (t.includes('/')) return t;
    }
    return '';
}

function firstPreviewImage(fileObj) {
    if (!fileObj || typeof fileObj !== 'object') return '';
    const arr = fileObj.previewImages ?? fileObj.preview_images;
    if (!Array.isArray(arr) || arr.length === 0) return '';
    const first = arr[0];
    return typeof first === 'string' ? first.trim() : '';
}

function linkDisplayTitle(urlStr) {
    const s = String(urlStr || '').trim();
    if (!s) return 'Link';
    try {
        const u = new URL(s);
        return u.hostname || s;
    } catch {
        return s.length > 72 ? `${s.slice(0, 69)}…` : s;
    }
}

/**
 * API returns **messages** with `assets: { media, docs, links }`. Links can be strings; media/docs are file objects.
 * Flatten to one row per file/link for the list UI.
 */
function flattenMessagesToAssetRows(items, assetType) {
    if (!Array.isArray(items)) return [];

    const out = [];

    items.forEach((msg, msgIdx) => {
        const messageId = msg?.id ?? msg?._id ?? `msg-${msgIdx}`;
        const sentAt = msg?.createdAt ?? msg?.created_at ?? null;
        const senderName = String(
            msg?.senderName ?? msg?.sender?.name ?? msg?.sender?.email ?? msg?.userName ?? '',
        ).trim();

        const assets = msg?.assets;
        if (assets && typeof assets === 'object' && !Array.isArray(assets)) {
            const bucket = assets[assetType];
            if (!Array.isArray(bucket)) return;

            bucket.forEach((entry, i) => {
                if (typeof entry === 'string') {
                    const url = entry.trim();
                    if (!url) return;
                    out.push({
                        id: `${messageId}-link-${i}`,
                        url,
                        name: linkDisplayTitle(url),
                        mimeType: '',
                        sentAt,
                        senderName,
                        previewImageUrl: '',
                        raw: msg,
                    });
                } else if (entry && typeof entry === 'object') {
                    const url = pickUrl(entry);
                    const name = pickName(entry);
                    const mimeType = pickMimeType(entry);
                    const previewImageUrl = firstPreviewImage(entry);
                    out.push({
                        id: `${messageId}-${i}-${url || name || 'file'}`,
                        url,
                        name,
                        mimeType,
                        sentAt,
                        senderName,
                        previewImageUrl,
                        raw: { message: msg, file: entry },
                    });
                }
            });
            return;
        }

        const url = pickUrl(msg);
        const name = pickName(msg);
        if (url || (name && name !== 'Attachment')) {
            out.push({
                id: String(messageId),
                url,
                name,
                mimeType: pickMimeType(msg),
                sentAt,
                senderName,
                previewImageUrl: firstPreviewImage(msg),
                raw: msg,
            });
        }
    });

    return out;
}

function getQueryErrorMessage(error) {
    const data = error?.response?.data;
    const msg = data?.message ?? data?.error ?? data?.msg;
    if (msg && typeof msg === 'string') return msg;
    if (Array.isArray(data?.errors) && data.errors.length) return String(data.errors[0]);
    return error?.message || String(error ?? '');
}

function isImageMime(mime, name, url) {
    const m = String(mime || '').toLowerCase();
    if (m.startsWith('image/')) return true;
    const s = `${name} ${url}`.toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?|$)/i.test(s);
}

function isVideoMime(mime, name, url) {
    const m = String(mime || '').toLowerCase();
    if (m.startsWith('video/')) return true;
    const s = `${name} ${url}`.toLowerCase();
    return /\.(mp4|webm|mov|mkv|avi|m4v)(\?|$)/i.test(s);
}

function isAudioMime(mime, name, url) {
    const m = String(mime || '').toLowerCase();
    if (m.startsWith('audio/')) return true;
    const s = `${name} ${url}`.toLowerCase();
    return /\.(mp3|wav|ogg|m4a|aac|flac|webm)(\?|$)/i.test(s);
}

/**
 * Right-side drawer: group summary scrolls with content; Media/Docs/Links bar is sticky with a light shadow when pinned.
 * Assets: GET …/messages/assets + pagination.
 */
export function ChatAssetsDrawer({
    open,
    onClose,
    chatId,
    title: drawerTitle = 'Shared in chat',
    /** When the drawer opens, select Media | Docs | Links. */
    defaultTab = 'media',
    /** When true, render `aboutContent` above the Media/Docs/Links tabs (e.g. group meta). */
    showAboutTab = false,
    aboutContent = null,
}) {
    const { token } = antTheme.useToken();
    const [activeTab, setActiveTab] = useState('media');
    const [page, setPage] = useState(1);
    const scrollBodyRef = useRef(null);
    const [scrollRootEl, setScrollRootEl] = useState(null);
    const aboutBlockRef = useRef(null);
    const [tabsStuck, setTabsStuck] = useState(false);

    const setScrollBodyRef = useCallback((node) => {
        scrollBodyRef.current = node;
        setScrollRootEl(node);
    }, []);

    const updateTabsStuck = useCallback(() => {
        const el = scrollBodyRef.current;
        if (!el) return;
        const hasAbout = Boolean(showAboutTab && aboutContent);
        if (!hasAbout) {
            setTabsStuck(false);
            return;
        }
        const aboutH = aboutBlockRef.current?.offsetHeight ?? 0;
        setTabsStuck(aboutH > 0 && el.scrollTop >= aboutH - 1);
    }, [showAboutTab, aboutContent]);

    const assetQueryType = useMemo(() => (ASSET_TAB_KEYS.includes(activeTab) ? activeTab : 'media'), [activeTab]);

    useEffect(() => {
        if (!open) {
            setPage(1);
            return;
        }
        const want = String(defaultTab || 'media');
        const next = ASSET_TAB_KEYS.includes(want) ? want : 'media';
        setActiveTab(next);
        setPage(1);
    }, [open, defaultTab]);

    const { data, isLoading, isError, error, isFetching } = useChatMessageAssets({
        chatId,
        assetType: assetQueryType,
        page,
        limit: PAGE_LIMIT,
        enabled: Boolean(open && chatId),
    });

    const rows = useMemo(() => flattenMessagesToAssetRows(data?.items, assetQueryType), [data?.items, assetQueryType]);
    const total = typeof data?.total === 'number' ? data.total : rows.length;

    const segmentedOptions = useMemo(
        () =>
            ASSET_TAB_CONFIG.map((t) => ({
                value: t.key,
                label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        {t.icon}
                        {t.label}
                    </span>
                ),
            })),
        [],
    );

    const listLoading = isLoading && !data;

    useEffect(() => {
        const el = scrollBodyRef.current;
        if (!el) return;
        updateTabsStuck();
        el.addEventListener('scroll', updateTabsStuck, { passive: true });
        const ro =
            typeof ResizeObserver !== 'undefined' && aboutBlockRef.current
                ? new ResizeObserver(() => updateTabsStuck())
                : null;
        if (aboutBlockRef.current && ro) ro.observe(aboutBlockRef.current);
        return () => {
            el.removeEventListener('scroll', updateTabsStuck);
            ro?.disconnect();
        };
    }, [open, updateTabsStuck, showAboutTab, aboutContent, activeTab]);

    useEffect(() => {
        if (open && scrollBodyRef.current) {
            scrollBodyRef.current.scrollTop = 0;
        }
    }, [open]);

    return (
        <Drawer
            title={drawerTitle}
            placement="right"
            width={Math.min(520, typeof window !== 'undefined' ? window.innerWidth - 24 : 520)}
            open={open}
            onClose={onClose}
            destroyOnClose
            styles={{
                header: {
                    marginBottom: 0,
                },
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: '100%',
                    overflow: 'hidden',
                    background: token.colorBgContainer,
                },
            }}
        >
            <div
                ref={setScrollBodyRef}
                style={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    overflow: 'auto',
                    overflowAnchor: 'none',
                    padding: `0 ${token.padding}px`,
                    position: 'relative',
                    isolation: 'isolate',
                }}
            >
                {showAboutTab && aboutContent ? (
                    <motion.div
                        ref={aboutBlockRef}
                        initial={{ opacity: 0.88, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={
                            scrollRootEl
                                ? { root: scrollRootEl, once: false, margin: '0px 0px -12% 0px', amount: 0.15 }
                                : { once: false, amount: 0.2 }
                        }
                        transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                        style={{ paddingTop: token.paddingSM, marginBottom: token.paddingMD }}
                    >
                        {aboutContent}
                    </motion.div>
                ) : null}

                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 30,
                        marginLeft: -token.padding,
                        marginRight: -token.padding,
                        paddingLeft: token.padding,
                        paddingRight: token.padding,
                        paddingTop: token.paddingSM,
                        paddingBottom: token.paddingSM,
                        background: token.colorBgContainer,
                        borderBottom: `1px solid ${token.colorSplit}`,
                        boxShadow: tabsStuck ? (token.boxShadowSecondary ?? '0 6px 16px -8px rgba(0,0,0,0.16)') : 'none',
                        transition: 'box-shadow 0.24s ease',
                    }}
                >
                    <Segmented
                        block
                        options={segmentedOptions}
                        value={activeTab}
                        onChange={(key) => {
                            setActiveTab(String(key));
                            setPage(1);
                        }}
                        size="middle"
                    />
                </div>
                {isError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="Could not load attachments"
                        description={getQueryErrorMessage(error)}
                        style={{ marginBottom: token.margin }}
                    />
                ) : null}

                {listLoading ? (
                    <Flex align="center" justify="center" style={{ minHeight: 220 }}>
                        <Spin size="large" />
                    </Flex>
                ) : rows.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Nothing here yet for this category"
                        style={{ marginTop: 48 }}
                    />
                ) : (
                    <List
                        itemLayout="horizontal"
                        dataSource={rows}
                        loading={isFetching && !listLoading}
                        renderItem={(row) => {
                            const resolved = row.url ? resolveChatMediaUrl(row.url) : '';
                            const previewResolved = row.previewImageUrl
                                ? resolveChatMediaUrl(row.previewImageUrl)
                                : '';
                            const img = isImageMime(row.mimeType, row.name, row.url);
                            const vid = isVideoMime(row.mimeType, row.name, row.url);
                            const aud = isAudioMime(row.mimeType, row.name, row.url);
                            const iconSrc = !img && !vid ? getFileIconSrc({ name: row.name, url: row.url, mimeType: row.mimeType }) : null;
                            const isLinksTab = activeTab === 'links';
                            const showDocPreviewThumb = Boolean(previewResolved && !img);

                            return (
                                <List.Item
                                    key={row.id}
                                    style={{
                                        borderBlockEnd: `1px solid ${token.colorSplit}`,
                                        paddingBlock: token.paddingSM,
                                    }}
                                    actions={
                                        resolved
                                            ? [
                                                  <Button
                                                      key="dl"
                                                      type="link"
                                                      size="small"
                                                      icon={isLinksTab ? <LinkOutlined /> : <DownloadOutlined />}
                                                      href={resolved}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      download={isLinksTab ? undefined : row.name}
                                                  >
                                                      {isLinksTab ? 'Open link' : 'Open'}
                                                  </Button>,
                                              ]
                                            : []
                                    }
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <div
                                                style={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: token.borderRadiusLG,
                                                    overflow: 'hidden',
                                                    background: token.colorFillAlter,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    border: `1px solid ${token.colorBorderSecondary}`,
                                                }}
                                            >
                                                {img && resolved ? (
                                                    <img
                                                        src={resolved}
                                                        alt=""
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : showDocPreviewThumb ? (
                                                    <img
                                                        src={previewResolved}
                                                        alt=""
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : vid && resolved ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <video
                                                            src={resolved}
                                                            muted
                                                            playsInline
                                                            preload="metadata"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                        <PlayCircleOutlined
                                                            style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                margin: 'auto',
                                                                fontSize: 22,
                                                                color: 'rgba(255,255,255,0.92)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                    </div>
                                                ) : aud && resolved ? (
                                                    <AudioOutlined style={{ fontSize: 22, color: token.colorTextSecondary }} />
                                                ) : isLinksTab ? (
                                                    <LinkOutlined style={{ fontSize: 22, color: token.colorTextSecondary }} />
                                                ) : iconSrc ? (
                                                    <img src={iconSrc} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                                                ) : (
                                                    <FileOutlined style={{ fontSize: 22, color: token.colorTextSecondary }} />
                                                )}
                                            </div>
                                        }
                                        title={
                                            <Typography.Text ellipsis style={{ maxWidth: '100%', fontWeight: 600 }}>
                                                {row.name}
                                            </Typography.Text>
                                        }
                                        description={
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {row.senderName ? (
                                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                        {row.senderName}
                                                    </Typography.Text>
                                                ) : null}
                                                {isLinksTab && row.url ? (
                                                    <Typography.Text
                                                        type="secondary"
                                                        copyable={{ text: row.url }}
                                                        ellipsis
                                                        style={{ fontSize: 11, maxWidth: '100%', display: 'block' }}
                                                        title={row.url}
                                                    >
                                                        {row.url}
                                                    </Typography.Text>
                                                ) : null}
                                                {formatAssetTime(row.sentAt) ? (
                                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                        {formatAssetTime(row.sentAt)}
                                                    </Typography.Text>
                                                ) : null}
                                                {aud && resolved ? (
                                                    <audio
                                                        controls
                                                        src={resolved}
                                                        style={{ width: '100%', maxWidth: 280, marginTop: 6 }}
                                                    />
                                                ) : null}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                )}
            </div>

            {total > 0 ? (
                <div
                    style={{
                        flexShrink: 0,
                        padding: token.paddingSM,
                        borderTop: `1px solid ${token.colorSplit}`,
                        display: 'flex',
                        justifyContent: 'center',
                        background: token.colorBgContainer,
                    }}
                >
                    <Pagination
                        current={page}
                        pageSize={PAGE_LIMIT}
                        total={total}
                        showSizeChanger={false}
                        showTotal={(t) =>
                            `${rows.length} attachment${rows.length === 1 ? '' : 's'} · ${t} message${t === 1 ? '' : 's'}`
                        }
                        onChange={(p) => setPage(p)}
                        disabled={listLoading}
                        size="small"
                    />
                </div>
            ) : null}
        </Drawer>
    );
}
